import { useQuery } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import { useAppDispatch } from 'src/app/hooks';
import {
  IStateNewsLetter,
  reducerUpdateDataCustomTemplate,
  reducerUpdateDataEmailSetting,
  reducerUpdateDataSelectDataSource
} from 'src/features/customNewsLetters';
import useQueryUrl from 'src/hooks/useQuery';
import {
  IGetNewsLetterDetailByIdResponse,
  INewsLetterTemplate
} from 'src/models/newsLetter';
import { GET_NEWS_LETTER_DETAIL_BY_ID } from 'src/services/graphql/customNewsLetter/query';

const useGetDetailNewsLetter = () => {
  const query = useQueryUrl();
  const dispatch = useAppDispatch();

  /**
   * Get news letter details
   */
  const newsLetterId = query.get('id');
  const {
    data: newsLetter,
    loading: newsLetterLoading,
    refetch: refetchNewsLetter,
    error: newsLetterError
  } = useQuery<IGetNewsLetterDetailByIdResponse, { newsLetterId: string }>(
    GET_NEWS_LETTER_DETAIL_BY_ID,
    {
      variables: { newsLetterId },
      skip: !newsLetterId,
      fetchPolicy: 'network-only'
    }
  );

  const transforNewsLeterData: INewsLetterTemplate | null =
    React.useMemo(() => {
      if (newsLetter?.newsLetter_getNewsLetterById?.newsLetter) {
        const data = newsLetter.newsLetter_getNewsLetterById?.newsLetter;
        const dataSocialMediaStreams =
          newsLetter.newsLetter_getNewsLetterById?.socialMediaStream;
        const dataOnlineNewsStreams =
          newsLetter.newsLetter_getNewsLetterById?.onlineNewsStream;
        const dataPrintStreams =
          newsLetter.newsLetter_getNewsLetterById?.printStream;
        const dataBroadcastStreams =
          newsLetter.newsLetter_getNewsLetterById?.broadcastStream;

        const stepOne: IStateNewsLetter['selectedNewsLetter']['customizeTemplate'] =
          {
            //step 1 -- content setting
            titleColor: data?.titleColor,
            isSourceIcon: false,
            ...(data?.readMoreLabel && { isSeedMoreButton: true }),
            ...(data?.readMoreLabel && { readMoreLabel: data?.readMoreLabel }),
            fontColor: data?.fontColor,
            isLikeMetric: data?.isLike,
            isViewMetric: data?.isView,
            isCommentMetric: data?.isComment,
            isEngagementMetric: data?.isEngagement,
            isSentimentMetric: data?.isSentimenSocialMedia,
            isPrValue: data?.isPrValue,
            isNumberOfKeywords: data?.isKeyWord,
            isSentiment: data?.isSentiment,

            //step 1 -- content styles
            mainColor: data?.colorHeaderSection,
            isLogo: data?.logoUrl ? true : false,
            logoUrl: data?.logoUrl,
            isHeaderImage: data?.headerImageUrl ? true : false,
            headerImageUrl: data?.headerImageUrl,
            isPrimaryText: data?.primaryText ? true : false,
            primaryText: data?.primaryText,
            isSecondaryText: data?.secondaryText ? true : false,
            secondaryText: data?.secondaryText,
            footerBackgroudColor: data?.footerBackgroundColor,
            footerText: data?.footerText,
            isFooterSocialMedia:
              data?.instagramUrl ||
              data?.youtubeUrl ||
              data?.twitterUrl ||
              data?.tiktokUrl
                ? true
                : false,
            instagram: data?.instagramUrl,
            twitter: data?.twitterUrl,
            tiktok: data?.tiktokUrl,
            youtube: data?.youtubeUrl
          };

        const stepTwo: IStateNewsLetter['selectedNewsLetter']['selectDataSource'] =
          {
            startDate: moment().subtract(7, 'days').toDate(),
            endDate: moment().toDate(),
            dataSource: '',
            objectIds: [],
            objectGroupId: '',
            clippingId: '',
            // otherMediaId: '',
            printMediaId: '',
            broadcastId: '',
            orderBy: 'desc',
            sortBy: 'dateColumn',
            sourceType: 'social_media',
            selectedStreamSosmedList:
              dataSocialMediaStreams?.map(({ id, ...rest }) => ({
                ...rest,
                platform: rest.platform
              })) || [],
            selectedStreamOnlineMediaList:
              dataOnlineNewsStreams?.map(({ id, ...rest }) => ({
                ...rest
              })) || [],
            selectedStreamBroadcastList:
              dataBroadcastStreams?.map(({ id, ...rest }) => ({
                ...rest
              })) || [],
            selectedStreamPrintMediaList:
              dataPrintStreams?.map(({ id, ...rest }) => ({
                ...rest,
                platform: rest.platform
              })) || []
          };

        const stepThree: IStateNewsLetter['selectedNewsLetter']['emailSetting'] =
          {
            templateName: data?.templateName,
            emailSubject: data?.emailSubject,
            recipient: data?.recepientEmail,
            metric:
              data?.frequentyDelivery?.toLowerCase() == 'sendnow'
                ? 'sendNow'
                : 'schedule',
            timezone: data?.timezone,
            deliveryDate: String(data?.deliveryDateTime)
          };

        dispatch(reducerUpdateDataCustomTemplate(stepOne));
        dispatch(reducerUpdateDataSelectDataSource(stepTwo));
        dispatch(reducerUpdateDataEmailSetting(stepThree));

        return { ...newsLetter?.newsLetter_getNewsLetterById?.newsLetter };
      }
      return null;
    }, [newsLetter]);

  return {
    newsLetter: transforNewsLeterData,
    newsLetterLoading,
    refetchNewsLetter,
    newsLetterError
  };
};

export default useGetDetailNewsLetter;
